import { skipToken } from '@reduxjs/toolkit/query';
import { AiPromptVersionResponse } from '@ydistri/api-sdk';
import { DataTable, Text, YColumnsType } from '@ydistri/ds';
import { formatDateTime } from '@ydistri/generic-utils';
import { Input, Modal } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../store';
import { authorName } from '../ProjectAdministration/AiPrompts/aiPromptsLib';
import { useGetAiPromptVersionsQuery } from '../ProjectAdministration/AiPrompts/apiAiPrompts';
import { AvailableModals, closeModal } from './modalSlice';

const modalId = AvailableModals.AI_PROMPT_HISTORY;

const columns: YColumnsType<AiPromptVersionResponse>[] = [
  { title: 'Version', dataIndex: 'versionNumber', key: 'versionNumber' },
  { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
  {
    title: 'Changed',
    key: 'createdAt',
    render: (version: AiPromptVersionResponse) => formatDateTime(version.createdAt),
  },
  {
    title: 'Changed by',
    key: 'createdBy',
    render: (version: AiPromptVersionResponse) => authorName(version.createdBy),
  },
];

const AiPromptHistoryModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const aiPrompt = useSelector((state: ReduxState) => state.modal.modalData[modalId]?.aiPrompt);
  const [chosen, setChosen] = useState<number>();

  const { data, isFetching, isError } = useGetAiPromptVersionsQuery(
    aiPrompt?.aiPromptId ?? skipToken,
  );

  // The server sends them oldest first; the history reads newest first.
  const versions = useMemo(() => [...(data?.versions ?? [])].reverse(), [data]);
  const shown = versions.find(version => version.versionNumber === chosen) ?? versions.at(0);

  const handleClose = useCallback(() => dispatch(closeModal()), [dispatch]);

  if (!aiPrompt) return null;

  return (
    <Modal
      title={`History: ${aiPrompt.title}`}
      centered
      open={true}
      width="80%"
      onCancel={handleClose}
      footer={null}
    >
      {isError ? (
        <Text $type="danger">The history could not be loaded.</Text>
      ) : (
        <>
          <DataTable<AiPromptVersionResponse>
            id="ai-prompt-history"
            loading={isFetching}
            columns={columns}
            dataSource={versions}
            size="small"
            rowKey="versionNumber"
            pagination={false}
            showHeader={true}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: shown ? [shown.versionNumber] : [],
              onChange: keys => setChosen(Number(keys[0])),
            }}
          />
          <Input.TextArea
            readOnly
            value={shown?.content ?? ''}
            autoSize={{ minRows: 8, maxRows: 24 }}
            data-testid="ai-prompt-history-content"
          />
        </>
      )}
    </Modal>
  );
};

export default AiPromptHistoryModal;
